import React, { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';



import { 
    Container, 
    Box,
    TextField, 
    FormControl, 
    FormHelperText,  
    InputLabel, 
    Select, 
    MenuItem, 
    Card, 
    Typography, 
    Grid, 
    IconButton, 
    InputAdornment, 
    TableContainer,
    Table, TableBody, TableRow, TableCell, Paper  } from '@mui/material';


import { useAPIUnauthenticatedService  } from '../helpers/APICallsUnauthenticated';
import { InHouseCardHeader, InHouseCardContent } from '../components/CustomMUI';
import { UserCreationAsBusinessOwnerAttributes, CacheKeys } from '../helpers/DataStructures';
import { useAppCommands } from '../appCore/AppRuntimeReact';
import AsyncActionButton, { AsyncActionButtonAbortError } from '../components/AsyncActionButton';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import  DisplayOfferInGridWrapper  from '../components/DisplayOfferInGridWrapper';

  interface CreateUserAndOfferUnauthenticatedFormProps {
    onDone: () => void;
  }

const parseSignupOfferCashAmount = (value: unknown) => {
    const parsedValue = typeof value === 'string' ? Number(value.trim()) : Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

// REQ-037: signup reward controls use an escaped pound sign to avoid mojibake.
const SIGNUP_POUND_SIGN = '\u00A3';
  
const CreateUserAndOfferUnauthenticatedForm: React.FC<CreateUserAndOfferUnauthenticatedFormProps> = ({ onDone }) => {  

    const [offerData, setOfferData] = useState({      
        id: 0,
        theOffer: "",
        location: "Location",
        offeringFixedCashAmount: 50,
        startDateTime: '',
        endDateTime: '',
        user: { 
            givenName: '', 
            familyName: '', 
            profession: 'Profession', 
            companyName: 'Company Name',
            isMe: false,
            email: '',
            companyAdmin: true}
    });

    // useEffect(() => {
    //     if (currentFormState === FormState.FillOutForm) {
    //       setOfferData({
    //         id: 0,
    //         theOffer: "",
    //         location: "Location",
    //         offeringFixedCashAmount: 50,
    //         startDateTime: '',
    //         endDateTime: '',
    //         user: { 
    //           givenName: '', 
    //           familyName: '', 
    //           profession: 'Profession', 
    //           companyName: 'Company Name',
    //           isMe: false,
    //           email: '',
    //           wantsToMakeOffers: false
    //         }
    //       });
    //     }
    //   }, [currentFormState]);

    const usersEmailRef = useRef('');
    const { requestSignup } = useAppCommands();

    const { handleSubmit, control, formState: { errors }, getValues, setError, setValue } = useForm<UserCreationAsBusinessOwnerAttributes>({
        defaultValues: { offeringFixedCashAmount: 50 }
      });
    
    const queryClient = useQueryClient();

    const apiServiceInstance = useAPIUnauthenticatedService();

//   const { isLoading, isError, data: data_loggedInUser, error } = useQuery(
//     [CacheKeys.LOGGED_IN_USER], 
//     async () => {
      
//         const user = await apiServiceInstance.getAuthenticatedUser();
//         return user;      
//     }
//   ); 

  const { mutateAsync } =
    useMutation([CacheKeys.LOGGED_IN_USER], apiServiceInstance.createUserAsBusinessOwner,
    {
        onSuccess: () => {
            queryClient.invalidateQueries([CacheKeys.LOGGED_IN_USER]);
        }
    },
  );

  const submitUserAndOffer = handleSubmit(
    async (data: UserCreationAsBusinessOwnerAttributes) => {
      const parsedCashAmount = parseSignupOfferCashAmount(data.offeringFixedCashAmount);

      if (parsedCashAmount === null) {
        setError('offeringFixedCashAmount', {
          type: 'validate',
          message: 'Enter a valid cash amount',
        });
        throw new AsyncActionButtonAbortError();
      }

      if (parsedCashAmount > 995) {
        setError('offeringFixedCashAmount', {
          type: 'max',
          message: `Maximum amount is ${SIGNUP_POUND_SIGN}995`,
        });
        throw new AsyncActionButtonAbortError();
      }

      usersEmailRef.current = data.email;
      await mutateAsync({
        ...data,
        // REQ-032: signup-created offers must send a numeric reward amount to the API.
        offeringFixedCashAmount: parsedCashAmount,
        companyAdmin: true,
      });
    },
    async () => {
      const parsedCashAmount = parseSignupOfferCashAmount(getValues('offeringFixedCashAmount'));

      if (parsedCashAmount === null) {
        setError('offeringFixedCashAmount', {
          type: 'validate',
          message: 'Enter a valid cash amount',
        });
      } else if (parsedCashAmount > 995) {
        setError('offeringFixedCashAmount', {
          type: 'max',
          message: `Maximum amount is ${SIGNUP_POUND_SIGN}995`,
        });
      }

      throw new AsyncActionButtonAbortError();
    }
  );

  const adjustCashAmount = (delta: number) => {
    const currentValue = getValues("offeringFixedCashAmount");
    const parsedValue = parseSignupOfferCashAmount(currentValue);

    if (parsedValue === null) {
      // REQ-032: stepper controls must not coerce invalid typed signup amounts to zero.
      setError('offeringFixedCashAmount', {
        type: 'validate',
        message: 'Enter a valid cash amount',
      });
      return;
    }

    const newValue = Math.min(Math.max(parsedValue + delta, 0), 995);
    setValue("offeringFixedCashAmount", newValue, { shouldValidate: true });
    setOfferData((prevData) => ({
        ...prevData,
        offeringFixedCashAmount: newValue
    }));
  };

  return (
        <Container style={{  display: 'flex', justifyContent: 'center', alignItems: 'start', gap: '20px' }}>
              

        <Card variant="outlined" style={{ margin: '10px', padding: '10px', minWidth: '350px' }}>
            <InHouseCardHeader title="Welcome to In House" />
            <InHouseCardContent>      
                <Typography variant="cardSubtitle" component="div" style={{ marginBottom: '1em' }}>
                    Please provide your details...
                </Typography>
                <form>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                
                                name="givenName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'First name is required', maxLength: { value: 255, message: 'First name should be less than 255 characters'} }}
                                render={({ field }) => 
                                <TextField 
                                    {...field} 
                                    label="First Name" 
                                    error={Boolean(errors.givenName)} 
                                    helperText={errors.givenName?.message} 
                                    fullWidth
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setOfferData((prevData) => ({
                                          ...prevData,
                                          user: {
                                            ...prevData.user,
                                            givenName: e.target.value,
                                          },
                                        }));
                                      }}
                                />}
                            
                                
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="familyName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Last name is required', maxLength: { value: 255, message: 'Last name should be less than 255 characters'} }}
                                render={({ field }) => 
                                <TextField 
                                    {...field} 
                                    label="Last Name" 
                                    error={Boolean(errors.familyName)} 
                                    helperText={errors.familyName?.message} 
                                    fullWidth
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setOfferData((prevData) => ({
                                          ...prevData,
                                          user: {
                                            ...prevData.user,
                                            familyName: e.target.value,
                                          },
                                        }));
                                      }}
                                />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="email" // Specify the field name
                                control={control}
                                defaultValue=""
                                rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: 'Invalid email format',
                                },
                                maxLength: { value: 255, message: 'Email should be less than 255 characters' },
                                }}
                                render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Email"
                                    error={Boolean(errors.email)}
                                    helperText={errors.email?.message}
                                    fullWidth
                                />
                                )}
                            />
                            </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="profession"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Profession is required' }}
                                render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="profession-label">Profession</InputLabel>
                                    <Select
                                        {...field}
                                        labelId="profession-label"
                                        label="Profession"
                                        error={Boolean(errors.profession)}
                                        onChange={(e) => {
                                            field.onChange(e); 
                                            setOfferData((prevData) => ({
                                                ...prevData,
                                                user: {
                                                  ...prevData.user,
                                                  profession: e.target.value, 
                                                },
                                              }));
                                          }}
                                    > 
                                    <MenuItem value="estate agent">Estate Agent</MenuItem>
                                    <MenuItem value="surveyor">Surveyor</MenuItem>
                                    {/* REQ-039: signup profession labels must use the correct public spelling. */}
                                    <MenuItem value="conveyancer">Conveyancer</MenuItem>
                                    <MenuItem value="mortgage broker">Mortgage Broker</MenuItem>
                                    <MenuItem value="epc assessor">EPC Assessor</MenuItem>

                                    <MenuItem value="removal and storage">Removal and Storage</MenuItem>
                                    <MenuItem value="damp and timber">Damp and Timber</MenuItem>
                                    <MenuItem value="asbestos surveyors">Asbestos Surveyors</MenuItem>
                                    <MenuItem value="drain engineers">Drain Engineers</MenuItem>
                                    <MenuItem value="house cleaners">House Cleaners</MenuItem>  
                                    <MenuItem value="electrician">Electrician</MenuItem>  
                                    <MenuItem value="plumber">Plumber</MenuItem> 
                                    <MenuItem value="property maintenance">Property Maintenance</MenuItem> 

                                    </Select>
                                    {errors.profession && <FormHelperText error>{errors.profession.message}</FormHelperText>}
      
                                    
                                </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                        <Controller
                                name="companyName"
                                control={control}
                                
                                
                                rules={{ maxLength: { value: 255, message: 'Company name should be less than 255 characters'} }}
                                render={({ field }) => 
                                <TextField 
                                    {...field} 
                                    label="Company Name" 
                                    error={Boolean(errors.companyName)} 
                                    helperText={errors.companyName?.message} 
                                    fullWidth
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setOfferData((prevData) => ({
                                            ...prevData,
                                            user: {
                                              ...prevData.user,
                                              companyName: e.target.value, 
                                            },
                                          }));
                                      }}
                                />}
                            />
                        </Grid>

                        <Grid item xs={12}>

                            <Controller
                                name="offeringFixedCashAmount"
                                control={control}
                                rules={{ 
                                required: 'Cash amount is required',
                                // REQ-032/REQ-037: reject typed invalid reward values with stable currency text.
                                validate: (value) => {
                                    const parsedValue = parseSignupOfferCashAmount(value);
                                    if (parsedValue === null) {
                                        return 'Enter a valid cash amount';
                                    }

                                    return parsedValue <= 995 || `Maximum amount is ${SIGNUP_POUND_SIGN}995`;
                                },
                                min: {value: 0, message: "Can't offer less than zero"},
                                max: {value: 995, message: `Maximum amount is ${SIGNUP_POUND_SIGN}995`}
                                }}
                                render={({ field }) => {
                                    const parsedStepperValue = parseSignupOfferCashAmount(field.value);

                                    return (
                                
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField 
                                        {...field} 
                                        label="How much will you offer for a referral?" 
                                        error={Boolean(errors.offeringFixedCashAmount)} 
                                        helperText={errors.offeringFixedCashAmount?.message} 
                                        fullWidth 
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">{SIGNUP_POUND_SIGN}</InputAdornment>,
                                        }}
                                        
                                        onChange={(e) => {
                                            const parsedValue = parseSignupOfferCashAmount(e.target.value);
                                            field.onChange(e);
                                            if (parsedValue !== null) {
                                                setOfferData((prevData) => ({
                                                    ...prevData,
                                                    offeringFixedCashAmount: parsedValue
                                                }));
                                            }
                                        }}
                                    />
                                    <IconButton 
                                        aria-label="Increase cash amount"
                                        size="small"
                                        onClick={() => adjustCashAmount(5)}
                                        disabled={parsedStepperValue !== null && parsedStepperValue >= 995}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                    <IconButton 
                                    aria-label="Decrease cash amount"
                                    size="small"
                                    onClick={() => adjustCashAmount(-5)}
                                    disabled={parsedStepperValue !== null && parsedStepperValue <= 0}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    
                                </div>
                                
                                    );
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="location"
                                control={control}
                                defaultValue=""
                                
                                rules={{ 
                                    maxLength: { value: 255, message: 'Location should be less than 255 characters'}, 
                                    required: 'Area is required' 
                                }}
                                render={({ field }) => 
                                <TextField 
                                    {...field} 
                                    label="Location" 
                                    error={Boolean(errors.location)} 
                                    helperText={errors.location?.message} 
                                    fullWidth
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setOfferData((prevData) => ({
                                            ...prevData,
                                            location: e.target.value
                                          }));
                                      }}
                                />}
                            />
                        </Grid>

                            <Grid item xs={12}>                               
                                <Controller
                                    name="theOffer"
                                    control={control}
                                    defaultValue=""                                    
                                    rules={{ 
                                        maxLength: { value: 1000, message: 'offer should be less than 1000 characters'}
                                    }}
                                    render={({ field }) => 
                                    <TextField multiline rows={3} 
                                        {...field} 
                                        label="Services you provide" 
                                        error={Boolean(errors.theOffer)} 
                                        helperText={errors.theOffer?.message} 
                                        fullWidth
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setOfferData((prevData) => ({
                                                ...prevData,
                                                theOffer: e.target.value
                                            }));
                                        }}
                                    />}
                                />
                            </Grid>

                        <Grid item xs={12}  sx={{textAlign:'center'}}>
                            <AsyncActionButton
                              errorToast="Error"
                              fullWidth
                              idleLabel="Submit"
                              onAction={() => submitUserAndOffer()}
                              onAfterSuccessDisplay={async () => {
                                onDone();
                                await requestSignup({
                                  loginHint: usersEmailRef.current,
                                  returnTo: `${window.location.origin}/dashboard`,
                                  redirectUri: `${window.location.origin}/dashboard`,
                                });
                              }}
                              pendingLabel="Submitting..."
                              styleVariant="green"
                              successDisplayMs={300}
                              successLabel="Redirecting..."
                            />
                        </Grid>
                    </Grid>
                </form>
            </InHouseCardContent>
        </Card>

        <Box 
            style={{ position: 'sticky', top: 0, padding: '5px',  zIndex: 1000, width: '500px' }}        
            sx={{ display: { xs: 'none', md: 'block' } }}>  

            Preview of your listing...

            <TableContainer component={Paper} style={{ margin: '5px' }}>
            <Table>
            <TableBody>    
                
                <TableRow >
                    <TableCell colSpan={4}> 
                        <DisplayOfferInGridWrapper offer={offerData} onActionButtonClick={() => {}} />
                    </TableCell>
                </TableRow>           
            </TableBody>
            </Table>
            </TableContainer>
        </Box> 

        </Container>
  );
};

export default CreateUserAndOfferUnauthenticatedForm
